import { 
  Box, 
  Button, 
  VStack, 
  HStack, 
  FormControl, 
  FormLabel, 
  Input, 
  Textarea, 
  Select, 
  NumberInput, 
  NumberInputField, 
  NumberInputStepper, 
  NumberIncrementStepper, 
  NumberDecrementStepper,
  Switch,
  Text,
  Heading,
  Divider,
  IconButton,
  Badge,
  useToast,
  Tooltip,
  useColorModeValue,
  Card,
  CardBody,
  CardHeader,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Collapse,
  Flex,
  Icon,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  List,
  ListItem,
  Spinner,
  Radio,
  RadioGroup,
  Checkbox,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Alert,
  AlertIcon
} from '@chakra-ui/react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { DeleteIcon, AddIcon, DownloadIcon, AttachmentIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { MdRocket, MdMoreVert, MdInfo, MdLandscape, MdAutoStories, MdPeople, MdAssessment, MdSettings, MdFlag, MdCheckCircle } from 'react-icons/md';
import { AppHeader } from '../../v5/components/AppHeader';
import axios from 'axios';
import { API_BASE, getAuthHeaders } from '../../utils/api';

interface Agent {
  agent_name: string;
  role: string;
  capabilities: string[];
  traits: string[];
  knowledge?: string[];
  goals: string[];
  decision_style?: string;
  interaction_rules: string;
  evaluation_inputs?: string[];
  metrics?: Record<string, string>;
  weighting?: Record<string, number>;
  report_format?: string;
  observation_window?: string;
}

interface DynamicEvent {
  event_id: string;
  description: string;
  trigger_condition: string;
}

interface BranchPoint {
  branch_id: string;
  description: string;
  condition: string;
}

interface SimulationBlueprint {
  simulation_id: string;
  metadata: {
    name: string;
    theme: string;
    primary_objective: string;
    expected_duration_steps: number;
    difficulty_level: string;
    intended_agents: string[];
    evaluation_focus: string[];
    simulation_run_duration_min?: number;
  };
  environment: {
    type: string;
    state_variables: Record<string, string>;
    rules: string;
    locations: string[];
    dynamic_events: DynamicEvent[];
  };
  story_skeleton: {
    starting_context: string;
    key_stakeholders: string[];
    primary_conflicts: string[];
    branch_points: BranchPoint[];
    possible_outcomes: string[];
  };
  agents: Agent[];
  simulation_flow: {
    turn_definition: string;
    trigger_mechanisms: string[];
    action_resolution: string;
    conflict_resolution: string;
    branching_logic: string;
  };
  evaluation_guidance: {
    evaluation_agent_goals: string[];
    metrics: Record<string, string>;
    weighting: Record<string, number>;
    observation_window: string;
    report_structure: string[];
  };
  optional_metadata: {
    randomization_seed?: number;
    logging: boolean;
    narrative_tone: string;
    extensions: string[];
    enable_real_world_problems: boolean;
  };
}

interface CustomSimulationBuilderProps {
  onLaunch: (blueprint: SimulationBlueprint) => Promise<string>;
  onCancel: () => void;
  onSimulationReady: (sessionId: string) => void;
  editSimulationId?: string;
  isEditMode?: boolean;
  isReadOnly?: boolean;
  onSaveComplete?: () => void;
}

export function CustomSimulationBuilder({ 
  onLaunch, 
  onCancel, 
  onSimulationReady,
  editSimulationId,
  isEditMode = false,
  isReadOnly = false,
  onSaveComplete
}: CustomSimulationBuilderProps) {
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [availableSimulations, setAvailableSimulations] = useState<any[]>([]);
  const [loadingSimulations, setLoadingSimulations] = useState(false);
  const [hasEdited, setHasEdited] = useState(false);
  
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    metadata: true,
    environment: true,
    story: true,
    agents: true,
    evaluation: false,
    optional: false,
  });

  const [activeSection, setActiveSection] = useState<string>('metadata');
  const [sliderValue, setSliderValue] = useState<number>(2);
  const sliderTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [validatedBlueprint, setValidatedBlueprint] = useState<SimulationBlueprint | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [preparedSessionId, setPreparedSessionId] = useState<string | null>(null);
  
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [saveMode, setSaveMode] = useState<'update' | 'new'>('update');
  const [newSimulationName, setNewSimulationName] = useState('');
  const [isGlobal, setIsGlobal] = useState(false);
  const [isProgramming, setIsProgramming] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [originalSimulationName, setOriginalSimulationName] = useState('');
  const [canUpdateSimulation, setCanUpdateSimulation] = useState(false);

  const metadataRef = useRef<HTMLDivElement>(null);
  const environmentRef = useRef<HTMLDivElement>(null);
  const storyRef = useRef<HTMLDivElement>(null);
  const agentsRef = useRef<HTMLDivElement>(null);
  const evaluationRef = useRef<HTMLDivElement>(null);
  const optionalRef = useRef<HTMLDivElement>(null);

  const [blueprint, setBlueprint] = useState<SimulationBlueprint>({
    simulation_id: `sim_${Date.now()}`,
    metadata: {
      name: '',
      theme: '',
      primary_objective: '',
      expected_duration_steps: 10,
      difficulty_level: 'Medium',
      intended_agents: [],
      evaluation_focus: [],
      simulation_run_duration_min: undefined,
    },
    environment: {
      type: 'Office',
      state_variables: {},
      rules: '',
      locations: [],
      dynamic_events: [],
    },
    story_skeleton: {
      starting_context: '',
      key_stakeholders: [],
      primary_conflicts: [],
      branch_points: [],
      possible_outcomes: [],
    },
    agents: [],
    simulation_flow: {
      turn_definition: 'Each turn represents a 30-minute interval',
      trigger_mechanisms: ['time_step', 'agent_action'],
      action_resolution: '',
      conflict_resolution: '',
      branching_logic: '',
    },
    evaluation_guidance: {
      evaluation_agent_goals: [],
      metrics: {},
      weighting: {},
      observation_window: 'Full simulation',
      report_structure: ['Summary', 'Detailed Analysis', 'Recommendations'],
    },
    optional_metadata: {
      logging: true,
      narrative_tone: 'Professional',
      extensions: [],
      enable_real_world_problems: false,
    },
  });

  const [userAgent, setUserAgent] = useState<Agent>({
    agent_name: 'User',
    role: '',
    capabilities: [],
    traits: [],
    goals: [],
    interaction_rules: '',
  });

  const [evaluatorAgent, setEvaluatorAgent] = useState<Agent>({
    agent_name: 'Evaluation Agent',
    role: 'Observer and evaluator',
    capabilities: ['Observe', 'Assess', 'Report'],
    traits: ['Objective', 'Analytical'],
    goals: [],
    interaction_rules: '',
    metrics: {},
    weighting: {},
  });

  const [isLoadingEdit, setIsLoadingEdit] = useState(false);

  useEffect(() => {
    loadAvailableSimulations();
  }, []);

  useEffect(() => {
    if (isEditMode && editSimulationId) {
      loadEditSimulation(editSimulationId);
    }
  }, [isEditMode, editSimulationId]);

  const loadEditSimulation = async (simulationId: string) => {
    setIsLoadingEdit(true);
    try {
      const authHeaders = await getAuthHeaders();
      
      // Fetch both blueprint and simulation list to check edit permissions
      const [blueprintResponse, listResponse] = await Promise.all([
        axios.get(`${API_BASE}/simulations/blueprint/${simulationId}`, { headers: authHeaders }),
        axios.get(`${API_BASE}/simulations/list`, { headers: authHeaders })
      ]);
      
      const blueprintData = blueprintResponse.data.data;
      const listData = listResponse.data.data;
      const simulationsList = Array.isArray(listData) ? listData : listData?.simulations || [];
      
      // Find the simulation in the list to get is_editable
      const simulationInfo = simulationsList.find((sim: any) => sim.id === simulationId);
      const isSimulationEditable = simulationInfo?.is_editable || false;
      setCanUpdateSimulation(isSimulationEditable);
      
      // If user cannot update, default to save as new
      if (!isSimulationEditable) {
        setSaveMode('new');
      }
      
      if (blueprintData) {
        const templateAgents = blueprintData.agents || [];
        const userAgentData = templateAgents.find((a: Agent) => 
          a.agent_name.toLowerCase().includes('user') || a.agent_name.includes('(User)')
        );
        const evaluatorAgentData = templateAgents.find((a: Agent) => 
          a.agent_name.toLowerCase().includes('evaluat') || a.role?.toLowerCase().includes('evaluat')
        );
        const otherAgents = templateAgents.filter((a: Agent) => 
          !a.agent_name.toLowerCase().includes('user') && 
          !a.agent_name.toLowerCase().includes('evaluat') &&
          !a.role?.toLowerCase().includes('evaluat')
        );
        
        if (userAgentData) setUserAgent(userAgentData);
        if (evaluatorAgentData) setEvaluatorAgent(evaluatorAgentData);
        
        setBlueprint({
          simulation_id: simulationId,
          metadata: blueprintData.metadata || blueprint.metadata,
          environment: blueprintData.environment || blueprint.environment,
          story_skeleton: blueprintData.story_skeleton || blueprint.story_skeleton,
          agents: otherAgents,
          simulation_flow: blueprintData.simulation_flow || blueprint.simulation_flow,
          evaluation_guidance: blueprintData.evaluation_guidance || blueprint.evaluation_guidance,
          optional_metadata: blueprintData.optional_metadata || blueprint.optional_metadata,
        });
        
        if (blueprintData.metadata?.simulation_run_duration_min) {
          setSliderValue(blueprintData.metadata.simulation_run_duration_min);
        }
        
        setIsGlobal(blueprintData.is_global || false);
        setIsProgramming(blueprintData.is_programming || false);
        setTags(blueprintData.tags || []);
        setOriginalSimulationName(blueprintData.title || blueprintData.metadata?.name || '');
      }
    } catch (error) {
      console.error('Failed to load simulation for editing:', error);
      toast({
        title: 'Failed to load simulation',
        description: 'Could not fetch simulation data for editing',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoadingEdit(false);
    }
  };

  const loadAvailableSimulations = async () => {
    setLoadingSimulations(true);
    try {
      const authHeaders = await getAuthHeaders();
      const response = await axios.get(`${API_BASE}/simulations/list`, { headers: authHeaders });
      const data = response.data.data;
      // Handle both old format (array) and new format (object with simulations key)
      const simulationsList = Array.isArray(data) ? data : data?.simulations;
      setAvailableSimulations(simulationsList || []);
    } catch (error) {
      console.error('Failed to load simulations:', error);
      toast({
        title: 'Failed to load templates',
        description: 'Could not fetch pre-built simulations',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingSimulations(false);
    }
  };

  const loadTemplateSimulation = async (simulationId: string) => {
    if (!simulationId) return;
    
    try {
      const authHeaders = await getAuthHeaders();
      const response = await axios.get(`${API_BASE}/simulations/blueprint/${simulationId}`, { headers: authHeaders });
      const templateData = response.data.data;
      
      const templateAgents = templateData.agents || [];
      const userAgentData = templateAgents.find((a: Agent) => 
        a.agent_name.toLowerCase().includes('user') || a.agent_name.includes('(User)')
      );
      const evaluatorAgentData = templateAgents.find((a: Agent) => 
        a.agent_name.toLowerCase().includes('evaluat') || a.role?.toLowerCase().includes('evaluat')
      );
      const otherAgents = templateAgents.filter((a: Agent) => 
        !a.agent_name.toLowerCase().includes('user') && 
        !a.agent_name.toLowerCase().includes('evaluat') &&
        !a.role?.toLowerCase().includes('evaluat')
      );
      
      if (userAgentData) {
        setUserAgent(userAgentData);
      }
      
      if (evaluatorAgentData) {
        setEvaluatorAgent(evaluatorAgentData);
      }
      
      setBlueprint({
        simulation_id: `sim_${Date.now()}`,
        metadata: templateData.metadata || blueprint.metadata,
        environment: templateData.environment || blueprint.environment,
        story_skeleton: templateData.story_skeleton || blueprint.story_skeleton,
        agents: otherAgents,
        simulation_flow: templateData.simulation_flow || blueprint.simulation_flow,
        evaluation_guidance: templateData.evaluation_guidance || blueprint.evaluation_guidance,
        optional_metadata: templateData.optional_metadata || blueprint.optional_metadata,
      });
      
      // Update slider value if time limit is set in template
      if (templateData.metadata?.simulation_run_duration_min) {
        setSliderValue(templateData.metadata.simulation_run_duration_min);
      }
      
      setHasEdited(true);
      
      toast({
        title: 'Template loaded',
        description: 'Pre-built simulation loaded as template. You can now modify it.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to load template:', error);
      toast({
        title: 'Template load failed',
        description: 'Could not load the selected simulation as template',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const validateImportedBlueprint = (data: any): { valid: boolean; error?: string } => {
    if (!data || typeof data !== 'object') {
      return { valid: false, error: 'Invalid blueprint: Root must be an object' };
    }

    if (!data.metadata || typeof data.metadata !== 'object') {
      return { valid: false, error: 'Missing or invalid "metadata" object' };
    }

    if (!data.agents || !Array.isArray(data.agents)) {
      return { valid: false, error: 'Missing or invalid "agents" array' };
    }

    return { valid: true };
  };

  const handleFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const fileText = await file.text();
      const importedData = JSON.parse(fileText);
      
      // Validate imported data structure
      const validation = validateImportedBlueprint(importedData);
      if (!validation.valid) {
        throw new Error(validation.error);
      }
      
      const importedAgents = importedData.agents || [];
      const userAgentData = importedAgents.find((a: Agent) => 
        a.agent_name.toLowerCase().includes('user') || a.agent_name.includes('(User)')
      );
      const evaluatorAgentData = importedAgents.find((a: Agent) => 
        a.agent_name.toLowerCase().includes('evaluat') || a.role?.toLowerCase().includes('evaluat')
      );
      const otherAgents = importedAgents.filter((a: Agent) => 
        !a.agent_name.toLowerCase().includes('user') && 
        !a.agent_name.toLowerCase().includes('evaluat') &&
        !a.role?.toLowerCase().includes('evaluat')
      );
      
      if (userAgentData) {
        setUserAgent(userAgentData);
      }
      
      if (evaluatorAgentData) {
        setEvaluatorAgent(evaluatorAgentData);
      }
      
      setBlueprint({
        simulation_id: importedData.simulation_id || `sim_${Date.now()}`,
        metadata: importedData.metadata || blueprint.metadata,
        environment: importedData.environment || blueprint.environment,
        story_skeleton: importedData.story_skeleton || blueprint.story_skeleton,
        agents: otherAgents,
        simulation_flow: importedData.simulation_flow || blueprint.simulation_flow,
        evaluation_guidance: importedData.evaluation_guidance || blueprint.evaluation_guidance,
        optional_metadata: importedData.optional_metadata || blueprint.optional_metadata,
      });
      
      // Update slider value if time limit is set in imported data
      if (importedData.metadata?.simulation_run_duration_min) {
        setSliderValue(importedData.metadata.simulation_run_duration_min);
      }
      
      setHasEdited(true);
      
      toast({
        title: 'JSON imported',
        description: 'Simulation configuration has been loaded',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Import failed:', error);
      let errorMessage = 'Failed to import configuration';
      
      if (error instanceof SyntaxError) {
        errorMessage = `Invalid JSON format: ${error.message}`;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Import failed',
        description: errorMessage,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleExport = () => {
    const finalUserAgent = {
      ...userAgent,
      agent_name: userAgent.agent_name.includes('(User)') 
        ? userAgent.agent_name 
        : `${userAgent.agent_name} (User)`,
    };

    const exportBlueprint = {
      ...blueprint,
      agents: [finalUserAgent, ...blueprint.agents, evaluatorAgent],
    };

    const dataStr = JSON.stringify(exportBlueprint, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${blueprint.simulation_id}.json`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast({
      title: 'Simulation exported',
      description: 'Your simulation blueprint has been downloaded as JSON',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const validateAndLaunch = () => {
    if (!blueprint.metadata.name) {
      toast({
        title: 'Validation Error',
        description: 'Simulation name is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (!blueprint.metadata.primary_objective) {
      toast({
        title: 'Validation Error',
        description: 'Primary objective is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!userAgent.role || userAgent.role.trim() === '') {
      toast({
        title: 'Validation Error',
        description: 'User role is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (blueprint.agents.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'At least one additional agent (besides User and Evaluator) is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const finalUserAgent = {
      ...userAgent,
      agent_name: userAgent.agent_name.includes('(User)') 
        ? userAgent.agent_name 
        : `${userAgent.agent_name} (User)`,
    };

    const finalEvaluatorAgent = {
      ...evaluatorAgent,
      agent_name: 'Evaluation Agent',
    };

    const finalBlueprint = {
      ...blueprint,
      agents: [finalUserAgent, ...blueprint.agents, finalEvaluatorAgent],
    };

    // Store the validated blueprint and show the modal
    setValidatedBlueprint(finalBlueprint);
    setIsModalOpen(true);
    
    // Immediately start initializing the simulation in the background
    initializeSimulation(finalBlueprint);
  };

  const initializeSimulation = async (finalBlueprint: SimulationBlueprint) => {
    setIsInitializing(true);
    setPreparedSessionId(null);
    
    try {
      const sessionId = await onLaunch(finalBlueprint);
      setPreparedSessionId(sessionId);
      setIsInitializing(false);
    } catch (error: any) {
      console.error('Failed to initialize simulation:', error);
      setIsInitializing(false);
      
      let errorMessage = 'Could not prepare the simulation. Please try again.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: 'Initialization failed',
        description: errorMessage,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    }
  };

  const validateBeforeSave = (): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (!blueprint.metadata.name?.trim()) {
      errors.push('Simulation name is required');
    }
    
    if (!blueprint.metadata.primary_objective?.trim()) {
      errors.push('Primary objective is required');
    }
    
    if (!userAgent.role?.trim()) {
      errors.push('User role is required');
    }
    
    if (blueprint.agents.length === 0) {
      errors.push('At least one agent is required');
    }
    
    return { valid: errors.length === 0, errors };
  };

  const openSaveModal = () => {
    const validation = validateBeforeSave();
    if (!validation.valid) {
      toast({
        title: 'Validation Error',
        description: validation.errors.join('. '),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    // Determine save mode:
    // - If user cannot update (read-only or no permission), always save as new
    // - If user can update, default to 'update'
    const shouldSaveAsNew = !canUpdateSimulation || isReadOnly;
    setSaveMode(shouldSaveAsNew ? 'new' : 'update');
    
    // Set default new simulation name
    if (shouldSaveAsNew) {
      setNewSimulationName(`${blueprint.metadata.name || ''} (Copy)`);
    } else {
      setNewSimulationName(blueprint.metadata.name || '');
    }
    
    setIsSaveModalOpen(true);
  };

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSaveEdit = async () => {
    // Check if user is trying to update but doesn't have permission
    if (saveMode === 'update' && !canUpdateSimulation) {
      toast({
        title: 'Permission denied',
        description: 'You do not have permission to update this simulation. Please save as a new simulation.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    const finalUserAgent = {
      ...userAgent,
      agent_name: userAgent.agent_name.includes('(User)') 
        ? userAgent.agent_name 
        : `${userAgent.agent_name} (User)`,
    };

    const finalEvaluatorAgent = {
      ...evaluatorAgent,
      agent_name: 'Evaluation Agent',
    };

    const finalBlueprint = {
      ...blueprint,
      agents: [finalUserAgent, ...blueprint.agents, finalEvaluatorAgent],
    };

    const simulationName = saveMode === 'new' ? newSimulationName : blueprint.metadata.name;
    
    if (saveMode === 'new' && (!newSimulationName.trim() || newSimulationName.trim() === originalSimulationName)) {
      toast({
        title: 'Invalid name',
        description: 'Please enter a unique name for the new simulation',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    
    try {
      const authHeaders = await getAuthHeaders();
      
      if (saveMode === 'update' && isEditMode && editSimulationId) {
        // Double-check we can update by verifying canUpdateSimulation is still true
        if (!canUpdateSimulation) {
          throw new Error('You do not have permission to update this simulation. Please save as a new simulation instead.');
        }
        
        await axios.put(`${API_BASE}/custom-simulations/${editSimulationId}`, {
          title: originalSimulationName, // Use original name when updating
          description: blueprint.metadata.primary_objective,
          role: userAgent.role,
          difficulty: blueprint.metadata.difficulty_level,
          duration: blueprint.metadata.simulation_run_duration_min 
            ? `${blueprint.metadata.simulation_run_duration_min} minutes` 
            : '30 minutes',
          blueprint: finalBlueprint,
          is_global: isGlobal,
          is_programming: isProgramming,
          tags: tags,
        }, { headers: authHeaders });
        
        toast({
          title: 'Simulation updated',
          description: 'Your changes have been saved successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await axios.post(`${API_BASE}/custom-simulations`, {
          title: simulationName,
          description: blueprint.metadata.primary_objective,
          role: userAgent.role,
          difficulty: blueprint.metadata.difficulty_level,
          duration: blueprint.metadata.simulation_run_duration_min 
            ? `${blueprint.metadata.simulation_run_duration_min} minutes` 
            : '30 minutes',
          blueprint: finalBlueprint,
          is_global: isGlobal,
          is_programming: isProgramming,
          tags: tags,
        }, { headers: authHeaders });
        
        toast({
          title: 'Simulation created',
          description: 'Your new simulation has been saved successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      
      setIsSaveModalOpen(false);
      
      if (onSaveComplete) {
        onSaveComplete();
      }
    } catch (error: any) {
      console.error('Failed to save simulation:', error);
      
      let errorTitle = 'Save failed';
      let errorMessage = 'Could not save changes. Please try again.';
      
      if (error.response?.status === 403) {
        errorTitle = 'Permission denied';
        errorMessage = error.response?.data?.error || 'You do not have permission to update this simulation. It may be a global simulation or belong to a different organization.';
      } else if (error.response?.status === 400) {
        errorTitle = 'Invalid request';
        errorMessage = error.response?.data?.error || 'Organization ID is required. Please ensure you are logged into an organization.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: errorTitle,
        description: errorMessage,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLaunchFromModal = () => {
    if (!preparedSessionId) return;
    setIsModalOpen(false);
    onSimulationReady(preparedSessionId);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setValidatedBlueprint(null);
    setPreparedSessionId(null);
    setIsInitializing(false);
  };

  const addAgent = () => {
    setBlueprint(prev => ({
      ...prev,
      agents: [...prev.agents, {
        agent_name: '',
        role: '',
        capabilities: [],
        traits: [],
        goals: [],
        interaction_rules: '',
      }],
    }));
  };

  const removeAgent = (index: number) => {
    setBlueprint(prev => ({
      ...prev,
      agents: prev.agents.filter((_, i) => i !== index),
    }));
  };

  const updateAgent = (index: number, field: keyof Agent, value: any) => {
    setHasEdited(true);
    setBlueprint(prev => ({
      ...prev,
      agents: prev.agents.map((agent, i) => 
        i === index ? { ...agent, [field]: value } : agent
      ),
    }));
  };

  const markEdited = () => {
    setHasEdited(true);
  };

  // Helper function to update blueprint and mark as edited
  const updateBlueprintWithEdit = (updater: (prev: SimulationBlueprint) => SimulationBlueprint) => {
    setBlueprint(updater);
    markEdited();
  };

  // Debounced slider handler for better performance
  const handleSliderChange = useCallback((value: number) => {
    setSliderValue(value);
    
    // Clear existing timeout
    if (sliderTimeoutRef.current) {
      clearTimeout(sliderTimeoutRef.current);
    }
    
    // Set new timeout to update blueprint after user stops sliding
    sliderTimeoutRef.current = setTimeout(() => {
      markEdited();
      setBlueprint(prev => ({
        ...prev,
        metadata: { ...prev.metadata, simulation_run_duration_min: value }
      }));
    }, 150); // 150ms debounce
  }, []);

  const addDynamicEvent = () => {
    setBlueprint(prev => ({
      ...prev,
      environment: {
        ...prev.environment,
        dynamic_events: [...prev.environment.dynamic_events, {
          event_id: `ev_${prev.environment.dynamic_events.length + 1}`,
          description: '',
          trigger_condition: '',
        }],
      },
    }));
  };

  const removeDynamicEvent = (index: number) => {
    setBlueprint(prev => ({
      ...prev,
      environment: {
        ...prev.environment,
        dynamic_events: prev.environment.dynamic_events.filter((_, i) => i !== index),
      },
    }));
  };

  const addBranchPoint = () => {
    setBlueprint(prev => ({
      ...prev,
      story_skeleton: {
        ...prev.story_skeleton,
        branch_points: [...prev.story_skeleton.branch_points, {
          branch_id: `b_${prev.story_skeleton.branch_points.length + 1}`,
          description: '',
          condition: '',
        }],
      },
    }));
  };

  const removeBranchPoint = (index: number) => {
    setBlueprint(prev => ({
      ...prev,
      story_skeleton: {
        ...prev.story_skeleton,
        branch_points: prev.story_skeleton.branch_points.filter((_, i) => i !== index),
      },
    }));
  };

  const parseCommaSeparated = (value: string): string[] => {
    return value.split(',').map(s => s.trim()).filter(s => s.length > 0);
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const scrollToSection = (section: string, ref: React.RefObject<HTMLDivElement>) => {
    setActiveSection(section);
    if (!expandedSections[section]) {
      setExpandedSections(prev => ({ ...prev, [section]: true }));
    }
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        { id: 'metadata', ref: metadataRef },
        { id: 'environment', ref: environmentRef },
        { id: 'story', ref: storyRef },
        { id: 'agents', ref: agentsRef },
        { id: 'evaluation', ref: evaluationRef },
        { id: 'optional', ref: optionalRef },
      ];

      const scrollPosition = window.scrollY + 150;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.ref.current && section.ref.current.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  const addStateVariable = (key: string, value: string) => {
    if (key && value) {
      setBlueprint(prev => ({
        ...prev,
        environment: {
          ...prev.environment,
          state_variables: {
            ...prev.environment.state_variables,
            [key]: value,
          },
        },
      }));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation Header */}
      <AppHeader 
        activePage="studio"
      />

      <div className="flex">
        {/* Left Sidebar Navigation */}
        <div className="w-72 bg-slate-50 sticky top-16 h-[calc(100vh-64px)] overflow-y-auto p-4 pt-6">
          <div className="space-y-4">
            {/* Navigation Card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-900">Settings</h3>
                
                {/* Required Sections */}
                <div>
                  <p className="text-xs font-semibold text-red-500 mb-2 uppercase">Required</p>
                  <div className="space-y-1">
                    <button
                      onClick={() => scrollToSection('metadata', metadataRef)}
                      className={`w-full flex items-center space-x-2 px-3 py-2 text-sm rounded-md border-l-3 transition-colors ${
                        activeSection === 'metadata' 
                          ? 'bg-emerald-50 text-emerald-700 border-l-emerald-500 font-semibold' 
                          : 'text-slate-700 border-l-transparent hover:bg-slate-50'
                      }`}
                    >
                      <MdInfo className="w-4 h-4" />
                      <span>Metadata</span>
                    </button>
                    
                    <button
                      onClick={() => scrollToSection('environment', environmentRef)}
                      className={`w-full flex items-center space-x-2 px-3 py-2 text-sm rounded-md border-l-3 transition-colors ${
                        activeSection === 'environment' 
                          ? 'bg-emerald-50 text-emerald-700 border-l-emerald-500 font-semibold' 
                          : 'text-slate-700 border-l-transparent hover:bg-slate-50'
                      }`}
                    >
                      <MdLandscape className="w-4 h-4" />
                      <span>Environment</span>
                    </button>
                    
                    <button
                      onClick={() => scrollToSection('story', storyRef)}
                      className={`w-full flex items-center space-x-2 px-3 py-2 text-sm rounded-md border-l-3 transition-colors ${
                        activeSection === 'story' 
                          ? 'bg-emerald-50 text-emerald-700 border-l-emerald-500 font-semibold' 
                          : 'text-slate-700 border-l-transparent hover:bg-slate-50'
                      }`}
                    >
                      <MdAutoStories className="w-4 h-4" />
                      <span>Story Skeleton</span>
                    </button>
                    
                    <button
                      onClick={() => scrollToSection('agents', agentsRef)}
                      className={`w-full flex items-center space-x-2 px-3 py-2 text-sm rounded-md border-l-3 transition-colors ${
                        activeSection === 'agents' 
                          ? 'bg-emerald-50 text-emerald-700 border-l-emerald-500 font-semibold' 
                          : 'text-slate-700 border-l-transparent hover:bg-slate-50'
                      }`}
                    >
                      <MdPeople className="w-4 h-4" />
                      <span>Agents</span>
                    </button>
                  </div>
                </div>
                
                <hr className="border-slate-200" />
                
                {/* Recommended Sections */}
                <div>
                  <p className="text-xs font-semibold text-orange-500 mb-2 uppercase">Recommended</p>
                  <div className="space-y-1">
                    <button
                      onClick={() => scrollToSection('evaluation', evaluationRef)}
                      className={`w-full flex items-center space-x-2 px-3 py-2 text-sm rounded-md border-l-3 transition-colors ${
                        activeSection === 'evaluation' 
                          ? 'bg-emerald-50 text-emerald-700 border-l-emerald-500 font-semibold' 
                          : 'text-slate-700 border-l-transparent hover:bg-slate-50'
                      }`}
                    >
                      <MdAssessment className="w-4 h-4" />
                      <span>Evaluation</span>
                    </button>
                  </div>
                </div>
                
                <hr className="border-slate-200" />
                
                {/* Optional Section */}
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-2 uppercase">Optional</p>
                  <div className="space-y-1">
                    <button
                      onClick={() => scrollToSection('optional', optionalRef)}
                      className={`w-full flex items-center space-x-2 px-3 py-2 text-sm rounded-md border-l-3 transition-colors ${
                        activeSection === 'optional' 
                          ? 'bg-emerald-50 text-emerald-700 border-l-emerald-500 font-semibold' 
                          : 'text-slate-700 border-l-transparent hover:bg-slate-50'
                      }`}
                    >
                      <MdSettings className="w-4 h-4" />
                      <span>Optional Settings</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Main Content */}
      <div className="flex-1 p-6 overflow-y-auto bg-slate-50">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Header Card */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
            <div className="space-y-6">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center space-x-3 mb-2">
                    <h1 className="text-3xl font-bold text-slate-900">Studio</h1>
                    {isReadOnly && (
                      <span className="px-3 py-1 text-xs font-semibold bg-purple-100 text-purple-700 rounded-full">
                        Viewing Global Template
                      </span>
                    )}
                    {isEditMode && !isReadOnly && (
                      <span className="px-3 py-1 text-xs font-semibold bg-teal-100 text-teal-700 rounded-full">
                        Editing Custom Simulation
                      </span>
                    )}
                  </div>
                  <p className="text-slate-600">
                    {isReadOnly 
                      ? 'Viewing a global simulation template - save as new to customize'
                      : isEditMode 
                        ? 'Editing custom simulation - make changes and save'
                        : 'Create a tailored simulation experience by configuring all parameters'
                    }
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleFileImport}
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={onCancel}
                    className="flex items-center space-x-2 px-5 py-2.5 border border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <span>Cancel</span>
                  </button>
                  <button
                    onClick={openSaveModal}
                    className="flex items-center space-x-2 px-5 py-2.5 font-semibold rounded-lg transition-colors shadow-md bg-emerald-500 text-white hover:bg-emerald-600"
                  >
                    <MdCheckCircle className="w-5 h-5" />
                    <span>{isReadOnly ? 'Save as New' : 'Save'}</span>
                  </button>
                  <Tooltip label="Launch Simulation">
                    <button
                      onClick={validateAndLaunch}
                      className="flex items-center justify-center p-2.5 border border-slate-300 text-slate-700 font-semibold rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <MdRocket className="w-5 h-5" />
                    </button>
                  </Tooltip>
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      icon={<MdMoreVert />}
                      variant="outline"
                      size="lg"
                      aria-label="More options"
                      borderColor="slate.300"
                    />
                    <MenuList>
                      <MenuItem icon={<AttachmentIcon />} onClick={handleImport}>
                        Import JSON
                      </MenuItem>
                      <MenuItem 
                        icon={<DownloadIcon />} 
                        onClick={handleExport}
                        isDisabled={!hasEdited}
                      >
                        Export JSON
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </div>
              </div>

              {!isEditMode && !isReadOnly && (
                <>
                  <hr className="border-slate-200" />

                  {/* Template Selector */}
                  <div>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-sm font-semibold text-slate-900">Start with a Template</h3>
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">Optional</span>
                      </div>
                      <p className="text-sm text-slate-600">
                        Select a pre-built simulation to use as a starting point, then customize it
                      </p>
                      <select
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white text-slate-900"
                        onChange={(e) => loadTemplateSimulation(e.target.value)}
                        disabled={loadingSimulations}
                        defaultValue=""
                      >
                        <option value="" disabled>Choose a template simulation...</option>
                        {availableSimulations.map((sim) => (
                          <option key={sim.id} value={sim.id}>
                            {sim.title} - {sim.role}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}
              
              {isLoadingEdit && (
                <div className="flex items-center justify-center py-8">
                  <Spinner size="lg" color="emerald.500" />
                  <span className="ml-3 text-slate-600">Loading simulation...</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="space-y-6">
          {/* Metadata Section */}
          <div ref={metadataRef} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <button
              onClick={() => toggleSection('metadata')}
              className="w-full flex justify-between items-center p-5 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <MdInfo className="w-5 h-5 text-emerald-500" />
                <h3 className="text-lg font-semibold text-slate-900">Metadata</h3>
                <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">Required</span>
              </div>
              {expandedSections.metadata ? <ChevronUpIcon className="w-5 h-5 text-slate-400" /> : <ChevronDownIcon className="w-5 h-5 text-slate-400" />}
            </button>
            <Collapse in={expandedSections.metadata}>
              <div className="px-5 pb-5 border-t border-slate-100">
                <VStack spacing={4} align="stretch">
                  <FormControl isRequired>
                    <FormLabel>Simulation Name</FormLabel>
                    <Input
                      value={blueprint.metadata.name}
                      onChange={(e) => {
                        markEdited();
                        setBlueprint(prev => ({
                          ...prev,
                          metadata: { ...prev.metadata, name: e.target.value }
                        }));
                      }}
                      placeholder="Enter simulation name"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Theme</FormLabel>
                    <Input
                      value={blueprint.metadata.theme}
                      onChange={(e) => {
                        markEdited();
                        setBlueprint(prev => ({
                          ...prev,
                          metadata: { ...prev.metadata, theme: e.target.value }
                        }));
                      }}
                      placeholder="e.g., Human Resource Management, Project Management"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Primary Objective</FormLabel>
                    <Textarea
                      value={blueprint.metadata.primary_objective}
                      onChange={(e) => {
                        markEdited();
                        setBlueprint(prev => ({
                          ...prev,
                          metadata: { ...prev.metadata, primary_objective: e.target.value }
                        }));
                      }}
                      placeholder="Main goal of the simulation"
                      rows={3}
                    />
                  </FormControl>

                  <HStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>
                        Expected Duration (steps)
                        <Tooltip label="1 step = 30 minutes">
                          <Badge ml={2} colorScheme="blue">?</Badge>
                        </Tooltip>
                      </FormLabel>
                      <NumberInput
                        value={blueprint.metadata.expected_duration_steps}
                        onChange={(_, val) => {
                          markEdited();
                          setBlueprint(prev => ({
                            ...prev,
                            metadata: { ...prev.metadata, expected_duration_steps: val }
                          }));
                        }}
                        min={1}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Difficulty Level</FormLabel>
                      <Select
                        value={blueprint.metadata.difficulty_level}
                        onChange={(e) => {
                          markEdited();
                          setBlueprint(prev => ({
                            ...prev,
                            metadata: { ...prev.metadata, difficulty_level: e.target.value }
                          }));
                        }}
                      >
                        <option value="Easy">Easy</option>
                        <option value="Medium">Medium</option>
                        <option value="Hard">Hard</option>
                      </Select>
                    </FormControl>
                  </HStack>

                  <FormControl>
                    <HStack justify="space-between" mb={2}>
                      <FormLabel mb={0}>
                        Time Limit (minutes)
                        <Badge ml={2} colorScheme="gray">Optional</Badge>
                      </FormLabel>
                      <Badge colorScheme="brand" fontSize="md" px={3} py={1}>
                        {sliderValue || blueprint.metadata.simulation_run_duration_min || 2} min
                      </Badge>
                    </HStack>
                    <Box pt={2} pb={8} px={2}>
                      <Slider
                        value={sliderValue || blueprint.metadata.simulation_run_duration_min || 2}
                        onChange={handleSliderChange}
                        min={2}
                        max={45}
                        step={1}
                      >
                        <SliderMark value={2} mt={2} ml={-1} fontSize="xs" color="gray.500">
                          2
                        </SliderMark>
                        <SliderMark value={15} mt={2} ml={-2} fontSize="xs" color="gray.500">
                          15
                        </SliderMark>
                        <SliderMark value={30} mt={2} ml={-2} fontSize="xs" color="gray.500">
                          30
                        </SliderMark>
                        <SliderMark value={45} mt={2} ml={-2} fontSize="xs" color="gray.500">
                          45
                        </SliderMark>
                        <SliderTrack bg="gray.200">
                          <SliderFilledTrack bg="brand.500" />
                        </SliderTrack>
                        <SliderThumb boxSize={5} bg="brand.500" />
                      </Slider>
                    </Box>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Intended Agents (comma-separated)</FormLabel>
                    <Input
                      value={(blueprint.metadata.intended_agents || []).join(', ')}
                      onChange={(e) => {
                        markEdited();
                        setBlueprint(prev => ({
                          ...prev,
                          metadata: { ...prev.metadata, intended_agents: parseCommaSeparated(e.target.value) }
                        }));
                      }}
                      placeholder="User, Agent 1, Agent 2"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Evaluation Focus (comma-separated)</FormLabel>
                    <Input
                      value={(blueprint.metadata.evaluation_focus || []).join(', ')}
                      onChange={(e) => {
                        markEdited();
                        setBlueprint(prev => ({
                          ...prev,
                          metadata: { ...prev.metadata, evaluation_focus: parseCommaSeparated(e.target.value) }
                        }));
                      }}
                      placeholder="Leadership, Communication, Problem Solving"
                    />
                  </FormControl>
                </VStack>
              </div>
            </Collapse>
          </div>

          {/* Environment Section */}
          <div ref={environmentRef} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <button
              onClick={() => toggleSection('environment')}
              className="w-full flex justify-between items-center p-5 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <MdLandscape className="w-5 h-5 text-emerald-500" />
                <h3 className="text-lg font-semibold text-slate-900">Environment</h3>
                <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">Required</span>
              </div>
              {expandedSections.environment ? <ChevronUpIcon className="w-5 h-5 text-slate-400" /> : <ChevronDownIcon className="w-5 h-5 text-slate-400" />}
            </button>
            <Collapse in={expandedSections.environment}>
              <div className="px-5 pb-5 border-t border-slate-100">
                <VStack spacing={4} align="stretch">
                  <FormControl isRequired>
                    <FormLabel>Environment Type</FormLabel>
                    <Input
                      value={blueprint.environment.type}
                      onChange={(e) => {
                        markEdited();
                        setBlueprint(prev => ({
                          ...prev,
                          environment: { ...prev.environment, type: e.target.value }
                        }));
                      }}
                      placeholder="e.g., Office, Remote, Factory"
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Rules</FormLabel>
                    <Textarea
                      value={blueprint.environment.rules}
                      onChange={(e) => {
                        markEdited();
                        setBlueprint(prev => ({
                          ...prev,
                          environment: { ...prev.environment, rules: e.target.value }
                        }));
                      }}
                      placeholder="Constraints agents must follow"
                      rows={3}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Locations (comma-separated)</FormLabel>
                    <Input
                      value={blueprint.environment.locations.join(', ')}
                      onChange={(e) => {
                        markEdited();
                        setBlueprint(prev => ({
                          ...prev,
                          environment: { ...prev.environment, locations: parseCommaSeparated(e.target.value) }
                        }));
                      }}
                      placeholder="Office, Meeting Room, Break Room"
                    />
                  </FormControl>

                  <Box>
                    <FormLabel>State Variables</FormLabel>
                    <VStack spacing={2} align="stretch">
                      {Object.entries(blueprint.environment.state_variables).map(([key, value]) => (
                        <HStack key={key}>
                          <Input value={key} isReadOnly />
                          <Input 
                            value={value} 
                            onChange={(e) => addStateVariable(key, e.target.value)}
                          />
                          <IconButton
                            aria-label="Remove variable"
                            icon={<DeleteIcon />}
                            bg="red.500"
                            color="white"
                            _hover={{ bg: "red.600" }}
                            _active={{ bg: "red.700" }}
                            onClick={() => {
                              const newVars = { ...blueprint.environment.state_variables };
                              delete newVars[key];
                              setBlueprint(prev => ({
                                ...prev,
                                environment: { ...prev.environment, state_variables: newVars }
                              }));
                            }}
                            size="sm"
                          />
                        </HStack>
                      ))}
                      <HStack>
                        <Input
                          id="new-var-key"
                          placeholder="Variable name"
                        />
                        <Input
                          id="new-var-value"
                          placeholder="Value (low/medium/high or numeric)"
                        />
                        <IconButton
                          aria-label="Add variable"
                          icon={<AddIcon />}
                          onClick={() => {
                            const keyInput = document.getElementById('new-var-key') as HTMLInputElement;
                            const valueInput = document.getElementById('new-var-value') as HTMLInputElement;
                            if (keyInput && valueInput) {
                              addStateVariable(keyInput.value, valueInput.value);
                              keyInput.value = '';
                              valueInput.value = '';
                            }
                          }}
                          size="sm"
                          bg="brand.500"
                          color="white"
                          _hover={{ bg: "brand.600" }}
                          _active={{ bg: "brand.700" }}
                        />
                      </HStack>
                    </VStack>
                  </Box>

                  <Box>
                    <HStack justify="space-between" mb={2}>
                      <FormLabel mb={0}>Dynamic Events</FormLabel>
                      <Button 
                        size="sm" 
                        leftIcon={<AddIcon />} 
                        onClick={addDynamicEvent}
                        bg="brand.500"
                        color="white"
                        _hover={{ bg: "brand.600" }}
                        _active={{ bg: "brand.700" }}
                      >
                        Add Event
                      </Button>
                    </HStack>
                    <VStack spacing={3} align="stretch">
                      {blueprint.environment.dynamic_events.map((event, index) => (
                        <Box key={index} p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                          <VStack spacing={2} align="stretch">
                            <HStack justify="space-between">
                              <Text fontWeight="600">Event {index + 1}</Text>
                              <IconButton
                                aria-label="Remove event"
                                icon={<DeleteIcon />}
                                size="xs"
                                bg="red.500"
                                color="white"
                                _hover={{ bg: "red.600" }}
                                _active={{ bg: "red.700" }}
                                onClick={() => removeDynamicEvent(index)}
                              />
                            </HStack>
                            <Input
                              value={event.event_id}
                              onChange={(e) => {
                                const newEvents = [...blueprint.environment.dynamic_events];
                                newEvents[index].event_id = e.target.value;
                                setBlueprint(prev => ({
                                  ...prev,
                                  environment: { ...prev.environment, dynamic_events: newEvents }
                                }));
                              }}
                              placeholder="Event ID (e.g., ev_01)"
                              size="sm"
                            />
                            <Textarea
                              value={event.description}
                              onChange={(e) => {
                                const newEvents = [...blueprint.environment.dynamic_events];
                                newEvents[index].description = e.target.value;
                                setBlueprint(prev => ({
                                  ...prev,
                                  environment: { ...prev.environment, dynamic_events: newEvents }
                                }));
                              }}
                              placeholder="Event description"
                              size="sm"
                              rows={2}
                            />
                            <Input
                              value={event.trigger_condition}
                              onChange={(e) => {
                                const newEvents = [...blueprint.environment.dynamic_events];
                                newEvents[index].trigger_condition = e.target.value;
                                setBlueprint(prev => ({
                                  ...prev,
                                  environment: { ...prev.environment, dynamic_events: newEvents }
                                }));
                              }}
                              placeholder="Trigger condition (e.g., time_step == 3)"
                              size="sm"
                            />
                          </VStack>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                </VStack>
              </div>
            </Collapse>
          </div>

          {/* Story Skeleton Section */}
          <div ref={storyRef} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <button
              onClick={() => toggleSection('story')}
              className="w-full flex justify-between items-center p-5 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <MdAutoStories className="w-5 h-5 text-emerald-500" />
                <h3 className="text-lg font-semibold text-slate-900">Story Skeleton</h3>
                <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">Required</span>
              </div>
              {expandedSections.story ? <ChevronUpIcon className="w-5 h-5 text-slate-400" /> : <ChevronDownIcon className="w-5 h-5 text-slate-400" />}
            </button>
            <Collapse in={expandedSections.story}>
              <div className="px-5 pb-5 border-t border-slate-100">
                <VStack spacing={4} align="stretch">
                  <FormControl isRequired>
                    <FormLabel>Starting Context</FormLabel>
                    <Textarea
                      value={blueprint.story_skeleton.starting_context}
                      onChange={(e) => {
                        markEdited();
                        setBlueprint(prev => ({
                          ...prev,
                          story_skeleton: { ...prev.story_skeleton, starting_context: e.target.value }
                        }));
                      }}
                      placeholder="Initial narrative context"
                      rows={3}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Key Stakeholders (comma-separated)</FormLabel>
                    <Input
                      value={blueprint.story_skeleton.key_stakeholders.join(', ')}
                      onChange={(e) => {
                        markEdited();
                        setBlueprint(prev => ({
                          ...prev,
                          story_skeleton: { ...prev.story_skeleton, key_stakeholders: parseCommaSeparated(e.target.value) }
                        }));
                      }}
                      placeholder="User, Manager, Employee"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Primary Conflicts (comma-separated)</FormLabel>
                    <Textarea
                      value={blueprint.story_skeleton.primary_conflicts.join('\n')}
                      onChange={(e) => {
                        markEdited();
                        setBlueprint(prev => ({
                          ...prev,
                          story_skeleton: { ...prev.story_skeleton, primary_conflicts: e.target.value.split('\n').filter(s => s.trim()) }
                        }));
                      }}
                      placeholder="Enter each conflict on a new line"
                      rows={4}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Possible Outcomes (comma-separated)</FormLabel>
                    <Textarea
                      value={blueprint.story_skeleton.possible_outcomes.join('\n')}
                      onChange={(e) => {
                        markEdited();
                        setBlueprint(prev => ({
                          ...prev,
                          story_skeleton: { ...prev.story_skeleton, possible_outcomes: e.target.value.split('\n').filter(s => s.trim()) }
                        }));
                      }}
                      placeholder="Enter each outcome on a new line"
                      rows={3}
                    />
                  </FormControl>

                  <Box>
                    <HStack justify="space-between" mb={2}>
                      <FormLabel mb={0}>Branch Points</FormLabel>
                      <Button 
                        size="sm" 
                        leftIcon={<AddIcon />} 
                        onClick={addBranchPoint}
                        bg="brand.500"
                        color="white"
                        _hover={{ bg: "brand.600" }}
                        _active={{ bg: "brand.700" }}
                      >
                        Add Branch Point
                      </Button>
                    </HStack>
                    <VStack spacing={3} align="stretch">
                      {blueprint.story_skeleton.branch_points.map((branch, index) => (
                        <Box key={index} p={4} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
                          <VStack spacing={2} align="stretch">
                            <HStack justify="space-between">
                              <Text fontWeight="600">Branch Point {index + 1}</Text>
                              <IconButton
                                aria-label="Remove branch"
                                icon={<DeleteIcon />}
                                size="xs"
                                bg="red.500"
                                color="white"
                                _hover={{ bg: "red.600" }}
                                _active={{ bg: "red.700" }}
                                onClick={() => removeBranchPoint(index)}
                              />
                            </HStack>
                            <Input
                              value={branch.branch_id}
                              onChange={(e) => {
                                const newBranches = [...blueprint.story_skeleton.branch_points];
                                newBranches[index].branch_id = e.target.value;
                                setBlueprint(prev => ({
                                  ...prev,
                                  story_skeleton: { ...prev.story_skeleton, branch_points: newBranches }
                                }));
                              }}
                              placeholder="Branch ID (e.g., b_01)"
                              size="sm"
                            />
                            <Textarea
                              value={branch.description}
                              onChange={(e) => {
                                const newBranches = [...blueprint.story_skeleton.branch_points];
                                newBranches[index].description = e.target.value;
                                setBlueprint(prev => ({
                                  ...prev,
                                  story_skeleton: { ...prev.story_skeleton, branch_points: newBranches }
                                }));
                              }}
                              placeholder="Branch point description"
                              size="sm"
                              rows={2}
                            />
                            <Input
                              value={branch.condition}
                              onChange={(e) => {
                                const newBranches = [...blueprint.story_skeleton.branch_points];
                                newBranches[index].condition = e.target.value;
                                setBlueprint(prev => ({
                                  ...prev,
                                  story_skeleton: { ...prev.story_skeleton, branch_points: newBranches }
                                }));
                              }}
                              placeholder="Condition (e.g., decision in ['option1', 'option2'])"
                              size="sm"
                            />
                          </VStack>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                </VStack>
              </div>
            </Collapse>
          </div>

          {/* Agents Section */}
          <div ref={agentsRef} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <button
              onClick={() => toggleSection('agents')}
              className="w-full flex justify-between items-center p-5 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <MdPeople className="w-5 h-5 text-emerald-500" />
                <h3 className="text-lg font-semibold text-slate-900">Agents</h3>
                <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">Required</span>
              </div>
              {expandedSections.agents ? <ChevronUpIcon className="w-5 h-5 text-slate-400" /> : <ChevronDownIcon className="w-5 h-5 text-slate-400" />}
            </button>
            <Collapse in={expandedSections.agents}>
              <div className="px-5 pb-5 border-t border-slate-100">
                <VStack spacing={6} align="stretch">
                  {/* User Role */}
                  <Box>
                    <HStack mb={3}>
                      <Heading size="sm" color="text.primary">User Role</Heading>
                      <Badge colorScheme="red">Required</Badge>
                    </HStack>
                    <Box p={4} borderWidth="2px" borderRadius="lg" borderColor="blue.300" bg={useColorModeValue('blue.50', 'blue.900')}>
                      <VStack spacing={3} align="stretch">
                        <HStack spacing={3}>
                          <FormControl isRequired>
                            <FormLabel fontSize="sm">User Name</FormLabel>
                            <Input
                              value={userAgent.agent_name}
                              onChange={(e) => {
                                markEdited();
                                setUserAgent(prev => ({ ...prev, agent_name: e.target.value }));
                              }}
                              placeholder="e.g., HR Lead, Project Manager"
                              size="sm"
                            />
                          </FormControl>

                          <FormControl isRequired>
                            <FormLabel fontSize="sm">Role</FormLabel>
                            <Input
                              value={userAgent.role}
                              onChange={(e) => {
                                markEdited();
                                setUserAgent(prev => ({ ...prev, role: e.target.value }));
                              }}
                              placeholder="e.g., Primary decision-maker"
                              size="sm"
                            />
                          </FormControl>
                        </HStack>

                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Capabilities (comma-separated)</FormLabel>
                          <Input
                            value={userAgent.capabilities.join(', ')}
                            onChange={(e) => {
                              markEdited();
                              setUserAgent(prev => ({ ...prev, capabilities: parseCommaSeparated(e.target.value) }));
                            }}
                            placeholder="e.g., Decision Making, Communication"
                            size="sm"
                          />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Traits (comma-separated)</FormLabel>
                          <Input
                            value={userAgent.traits.join(', ')}
                            onChange={(e) => {
                              markEdited();
                              setUserAgent(prev => ({ ...prev, traits: parseCommaSeparated(e.target.value) }));
                            }}
                            placeholder="e.g., Analytical, Empathetic"
                            size="sm"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">Knowledge (comma-separated)</FormLabel>
                          <Input
                            value={(userAgent.knowledge || []).join(', ')}
                            onChange={(e) => {
                              markEdited();
                              setUserAgent(prev => ({ ...prev, knowledge: parseCommaSeparated(e.target.value) }));
                            }}
                            placeholder="e.g., Company Policies, Technical Skills"
                            size="sm"
                          />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Goals (comma-separated)</FormLabel>
                          <Input
                            value={userAgent.goals.join(', ')}
                            onChange={(e) => {
                              markEdited();
                              setUserAgent(prev => ({ ...prev, goals: parseCommaSeparated(e.target.value) }));
                            }}
                            placeholder="e.g., Complete project on time"
                            size="sm"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">Decision Style</FormLabel>
                          <Input
                            value={userAgent.decision_style || ''}
                            onChange={(e) => {
                              markEdited();
                              setUserAgent(prev => ({ ...prev, decision_style: e.target.value }));
                            }}
                            placeholder="e.g., Analytical, Intuitive, Collaborative"
                            size="sm"
                          />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Interaction Rules</FormLabel>
                          <Textarea
                            value={userAgent.interaction_rules}
                            onChange={(e) => {
                              markEdited();
                              setUserAgent(prev => ({ ...prev, interaction_rules: e.target.value }));
                            }}
                            placeholder="How this agent interacts with others"
                            size="sm"
                            rows={2}
                          />
                        </FormControl>
                      </VStack>
                    </Box>
                  </Box>

                  {/* Evaluator Role */}
                  <Box>
                    <HStack mb={3}>
                      <Heading size="sm" color="text.primary">Evaluator Role</Heading>
                      <Badge colorScheme="red">Required</Badge>
                    </HStack>
                    <Box p={4} borderWidth="2px" borderRadius="lg" borderColor="purple.300" bg={useColorModeValue('purple.50', 'purple.900')}>
                      <VStack spacing={3} align="stretch">
                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Role</FormLabel>
                          <Input
                            value={evaluatorAgent.role}
                            onChange={(e) => {
                              markEdited();
                              setEvaluatorAgent(prev => ({ ...prev, role: e.target.value }));
                            }}
                            placeholder="e.g., Observer and evaluator of performance"
                            size="sm"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">Goals (comma-separated)</FormLabel>
                          <Input
                            value={evaluatorAgent.goals.join(', ')}
                            onChange={(e) => {
                              markEdited();
                              setEvaluatorAgent(prev => ({ ...prev, goals: parseCommaSeparated(e.target.value) }));
                            }}
                            placeholder="e.g., Assess performance, Provide feedback"
                            size="sm"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">Interaction Rules</FormLabel>
                          <Textarea
                            value={evaluatorAgent.interaction_rules}
                            onChange={(e) => {
                              markEdited();
                              setEvaluatorAgent(prev => ({ ...prev, interaction_rules: e.target.value }));
                            }}
                            placeholder="How the evaluator observes and interacts"
                            size="sm"
                            rows={2}
                          />
                        </FormControl>
                      </VStack>
                    </Box>
                  </Box>

                  {/* Other Agents */}
                  <Box>
                    <HStack justify="space-between" mb={3}>
                      <HStack>
                        <Heading size="sm" color="text.primary">Other Agents</Heading>
                        <Badge colorScheme="red">Required (min 1)</Badge>
                      </HStack>
                      <Button 
                        leftIcon={<AddIcon />} 
                        onClick={addAgent} 
                        size="sm"
                        bg="brand.500"
                        color="white"
                        _hover={{ bg: "brand.600" }}
                        _active={{ bg: "brand.700" }}
                      >
                        Add Agent
                      </Button>
                    </HStack>
                    <Text fontSize="sm" color="text.secondary" mb={3}>
                      Add other agents that will interact in the simulation
                    </Text>
                  </Box>

                  {blueprint.agents.map((agent, index) => (
                    <Box key={index} p={4} borderWidth="2px" borderRadius="lg" borderColor={borderColor} bg={useColorModeValue('gray.50', 'gray.900')}>
                      <VStack spacing={3} align="stretch">
                        <HStack justify="space-between">
                          <Heading size="sm">Agent {index + 1}</Heading>
                          <IconButton
                            aria-label="Remove agent"
                            icon={<DeleteIcon />}
                            size="sm"
                            bg="red.500"
                            color="white"
                            _hover={{ bg: "red.600" }}
                            _active={{ bg: "red.700" }}
                            onClick={() => removeAgent(index)}
                          />
                        </HStack>

                        <HStack spacing={3}>
                          <FormControl isRequired>
                            <FormLabel fontSize="sm">Agent Name</FormLabel>
                            <Input
                              value={agent.agent_name}
                              onChange={(e) => updateAgent(index, 'agent_name', e.target.value)}
                              placeholder="e.g., Employee 1, Manager"
                              size="sm"
                            />
                          </FormControl>

                          <FormControl isRequired>
                            <FormLabel fontSize="sm">Role</FormLabel>
                            <Input
                              value={agent.role}
                              onChange={(e) => updateAgent(index, 'role', e.target.value)}
                              placeholder="e.g., Team Lead, Evaluator"
                              size="sm"
                            />
                          </FormControl>
                        </HStack>

                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Capabilities (comma-separated)</FormLabel>
                          <Input
                            value={agent.capabilities.join(', ')}
                            onChange={(e) => updateAgent(index, 'capabilities', parseCommaSeparated(e.target.value))}
                            placeholder="e.g., Decision Making, Communication"
                            size="sm"
                          />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Traits (comma-separated)</FormLabel>
                          <Input
                            value={agent.traits.join(', ')}
                            onChange={(e) => updateAgent(index, 'traits', parseCommaSeparated(e.target.value))}
                            placeholder="e.g., Analytical, Empathetic"
                            size="sm"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">Knowledge (comma-separated)</FormLabel>
                          <Input
                            value={(agent.knowledge || []).join(', ')}
                            onChange={(e) => updateAgent(index, 'knowledge', parseCommaSeparated(e.target.value))}
                            placeholder="e.g., Company Policies, Technical Skills"
                            size="sm"
                          />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Goals (comma-separated)</FormLabel>
                          <Input
                            value={agent.goals.join(', ')}
                            onChange={(e) => updateAgent(index, 'goals', parseCommaSeparated(e.target.value))}
                            placeholder="e.g., Complete project on time"
                            size="sm"
                          />
                        </FormControl>

                        <FormControl>
                          <FormLabel fontSize="sm">Decision Style</FormLabel>
                          <Input
                            value={agent.decision_style || ''}
                            onChange={(e) => updateAgent(index, 'decision_style', e.target.value)}
                            placeholder="e.g., Analytical, Intuitive, Collaborative"
                            size="sm"
                          />
                        </FormControl>

                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Interaction Rules</FormLabel>
                          <Textarea
                            value={agent.interaction_rules}
                            onChange={(e) => updateAgent(index, 'interaction_rules', e.target.value)}
                            placeholder="How this agent interacts with others"
                            size="sm"
                            rows={2}
                          />
                        </FormControl>
                      </VStack>
                    </Box>
                  ))}

                  {blueprint.agents.length === 0 && (
                    <Box p={8} textAlign="center" borderWidth="2px" borderStyle="dashed" borderRadius="lg" borderColor={borderColor}>
                      <Text color="text.secondary" mb={4}>No agents added yet</Text>
                      <Button 
                        leftIcon={<AddIcon />} 
                        onClick={addAgent}
                        bg="brand.500"
                        color="white"
                        _hover={{ bg: "brand.600" }}
                        _active={{ bg: "brand.700" }}
                      >
                        Add Your First Agent
                      </Button>
                    </Box>
                  )}
                </VStack>
              </div>
            </Collapse>
          </div>

          {/* Evaluation Guidance Section */}
          <div ref={evaluationRef} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <button
              onClick={() => toggleSection('evaluation')}
              className="w-full flex justify-between items-center p-5 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <MdAssessment className="w-5 h-5 text-emerald-500" />
                <h3 className="text-lg font-semibold text-slate-900">Evaluation Guidance</h3>
                <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded">Recommended</span>
              </div>
              {expandedSections.evaluation ? <ChevronUpIcon className="w-5 h-5 text-slate-400" /> : <ChevronDownIcon className="w-5 h-5 text-slate-400" />}
            </button>
            <Collapse in={expandedSections.evaluation}>
              <div className="px-5 pb-5 border-t border-slate-100">
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Evaluation Agent Goals (one per line)</FormLabel>
                    <Textarea
                      value={blueprint.evaluation_guidance.evaluation_agent_goals.join('\n')}
                      onChange={(e) => {
                        markEdited();
                        setBlueprint(prev => ({
                          ...prev,
                          evaluation_guidance: { 
                            ...prev.evaluation_guidance, 
                            evaluation_agent_goals: e.target.value.split('\n').filter(s => s.trim()) 
                          }
                        }));
                      }}
                      placeholder="Enter each goal on a new line"
                      rows={3}
                    />
                  </FormControl>
                </VStack>
              </div>
            </Collapse>
          </div>

          {/* Optional Metadata Section */}
          <div ref={optionalRef} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <button
              onClick={() => toggleSection('optional')}
              className="w-full flex justify-between items-center p-5 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <MdSettings className="w-5 h-5 text-emerald-500" />
                <h3 className="text-lg font-semibold text-slate-900">Optional Settings</h3>
                <span className="px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 rounded">Optional</span>
              </div>
              {expandedSections.optional ? <ChevronUpIcon className="w-5 h-5 text-slate-400" /> : <ChevronDownIcon className="w-5 h-5 text-slate-400" />}
            </button>
            <Collapse in={expandedSections.optional}>
              <div className="px-5 pb-5 border-t border-slate-100">
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>Randomization Seed</FormLabel>
                    <NumberInput
                      value={blueprint.optional_metadata.randomization_seed || ''}
                      onChange={(_, val) => {
                        markEdited();
                        setBlueprint(prev => ({
                          ...prev,
                          optional_metadata: { ...prev.optional_metadata, randomization_seed: isNaN(val) ? undefined : val }
                        }));
                      }}
                    >
                      <NumberInputField placeholder="Leave empty for random" />
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Narrative Tone</FormLabel>
                    <Select
                      value={blueprint.optional_metadata.narrative_tone}
                      onChange={(e) => {
                        markEdited();
                        setBlueprint(prev => ({
                          ...prev,
                          optional_metadata: { ...prev.optional_metadata, narrative_tone: e.target.value }
                        }));
                      }}
                    >
                      <option value="Professional">Professional</option>
                      <option value="Casual">Casual</option>
                      <option value="Formal">Formal</option>
                      <option value="Friendly">Friendly</option>
                    </Select>
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb={0}>Enable Logging</FormLabel>
                    <Switch
                      isChecked={blueprint.optional_metadata.logging}
                      onChange={(e) => {
                        markEdited();
                        setBlueprint(prev => ({
                          ...prev,
                          optional_metadata: { ...prev.optional_metadata, logging: e.target.checked }
                        }));
                      }}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb={0}>
                      Enable Real World Problems
                      <Tooltip label="Requires GEMINI_API_KEY environment variable">
                        <Badge ml={2} colorScheme="blue">?</Badge>
                      </Tooltip>
                    </FormLabel>
                    <Switch
                      isChecked={blueprint.optional_metadata.enable_real_world_problems}
                      onChange={(e) => {
                        markEdited();
                        setBlueprint(prev => ({
                          ...prev,
                          optional_metadata: { ...prev.optional_metadata, enable_real_world_problems: e.target.checked }
                        }));
                      }}
                    />
                  </FormControl>
                </VStack>
              </div>
            </Collapse>
          </div>
          </div>
        </div>
      </div>
      </div>

      {/* Instructions Modal */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} size="2xl" isCentered scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
        <ModalContent 
          bg={bgColor} 
          borderWidth="1px" 
          borderColor={borderColor} 
          shadow="deep"
          maxH="90vh"
        >
          <ModalHeader>
            <VStack align="stretch" spacing={2}>
              <HStack spacing={3}>
                <Icon as={MdRocket} boxSize={6} color="brand.500" />
                <Heading size="lg" fontWeight="700" color="text.primary">
                  {validatedBlueprint?.metadata.name}
                </Heading>
              </HStack>
              <Badge 
                colorScheme="blue" 
                fontSize="sm" 
                px={3} 
                py={1} 
                borderRadius="md"
                width="fit-content"
              >
                Your Role: {userAgent.role}
              </Badge>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          
          <ModalBody overflowY="auto" maxH="calc(90vh - 200px)">
            <VStack align="stretch" spacing={6}>
              <Box>
                <Text fontSize="md" fontWeight="600" color="text.primary" mb={2}>
                  Simulation Overview
                </Text>
                <Text color="text.secondary" lineHeight="1.7">
                  {validatedBlueprint?.metadata.theme}
                </Text>
              </Box>

              <Divider />

              <Box>
                <HStack spacing={2} mb={3}>
                  <Icon as={MdFlag} color="brand.500" />
                  <Text fontSize="md" fontWeight="600" color="text.primary">
                    Primary Objective
                  </Text>
                </HStack>
                <Text color="text.secondary" lineHeight="1.7">
                  {validatedBlueprint?.metadata.primary_objective}
                </Text>
              </Box>

              {userAgent.goals && userAgent.goals.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Text fontSize="md" fontWeight="600" color="text.primary" mb={3}>
                      Your Goals
                    </Text>
                    <List spacing={2}>
                      {userAgent.goals.map((goal, idx) => (
                        <ListItem key={idx} color="text.secondary">
                          <HStack align="start" spacing={2}>
                            <Icon as={MdCheckCircle} color="green.500" mt={1} />
                            <Text lineHeight="1.6">{goal}</Text>
                          </HStack>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </>
              )}

              {validatedBlueprint?.story_skeleton.primary_conflicts && 
               validatedBlueprint.story_skeleton.primary_conflicts.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Text fontSize="md" fontWeight="600" color="text.primary" mb={3}>
                      Key Challenges
                    </Text>
                    <List spacing={2}>
                      {validatedBlueprint.story_skeleton.primary_conflicts.map((conflict, idx) => (
                        <ListItem key={idx} color="text.secondary">
                          <HStack align="start" spacing={2}>
                            <Text color="orange.500" fontWeight="bold" mt={0.5}>•</Text>
                            <Text lineHeight="1.6">{conflict}</Text>
                          </HStack>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </>
              )}

              <Divider />

              <Box bg="brand.50" p={4} borderRadius="md" borderWidth="1px" borderColor="brand.200">
                <Text fontSize="sm" color="text.secondary" lineHeight="1.7">
                  <strong>Ready to begin?</strong> You'll interact with the simulation agents through a chat interface. 
                  Your decisions and responses will shape the outcome of the scenario.
                </Text>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button 
              variant="ghost" 
              mr={3} 
              onClick={handleCloseModal}
              color="text.secondary"
              isDisabled={isInitializing}
            >
              Go Back
            </Button>
            <Button
              variant="solid"
              leftIcon={isInitializing ? <Spinner size="sm" /> : <MdRocket />}
              onClick={handleLaunchFromModal}
              isDisabled={!preparedSessionId || isInitializing}
              isLoading={isInitializing}
              loadingText="Preparing..."
            >
              {isInitializing ? 'Preparing...' : "I'm Ready"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Save Simulation Modal - Tailwind Styled */}
      {isSaveModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => !isSaving && setIsSaveModalOpen(false)}
          />
          
          {/* Modal Content */}
          <div className="relative w-full max-w-lg mx-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Save Simulation</h2>
              <button
                onClick={() => !isSaving && setIsSaveModalOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                disabled={isSaving}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
              {/* Info alert for users who cannot update - must save as new */}
              {isEditMode && !canUpdateSimulation && (
                <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    {isReadOnly 
                      ? "This is a global simulation. Your changes will be saved as a new custom simulation."
                      : "You don't have permission to update this simulation. Your changes will be saved as a new custom simulation."}
                  </p>
                </div>
              )}

              {/* Save Mode - only show if user can update AND the simulation name has been changed */}
              {isEditMode && canUpdateSimulation && blueprint.metadata.name !== originalSimulationName && (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    Save Mode
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="saveMode"
                        value="update"
                        checked={saveMode === 'update'}
                        onChange={() => setSaveMode('update')}
                        className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Update existing simulation (keep original name)</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="saveMode"
                        value="new"
                        checked={saveMode === 'new'}
                        onChange={() => setSaveMode('new')}
                        className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Save as new simulation (with new name: "{blueprint.metadata.name}")</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Info when name changed but user selects update */}
              {isEditMode && canUpdateSimulation && blueprint.metadata.name !== originalSimulationName && saveMode === 'update' && (
                <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    The simulation will be updated with its original name: "{originalSimulationName}"
                  </p>
                </div>
              )}

              {/* Simulation Name input - only show when saving as new OR when user cannot update */}
              {(saveMode === 'new' || !isEditMode || !canUpdateSimulation) && (
                <div>
                  <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    New Simulation Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newSimulationName}
                    onChange={(e) => setNewSimulationName(e.target.value)}
                    placeholder="Enter a unique name for your simulation"
                    className="w-full px-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  />
                  {newSimulationName.trim() && newSimulationName.trim() === originalSimulationName && (
                    <p className="mt-1.5 text-sm text-amber-600 dark:text-amber-400">
                      Name must be different from the original simulation
                    </p>
                  )}
                </div>
              )}

              {/* Make globally available checkbox */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={isGlobal}
                  onChange={(e) => setIsGlobal(e.target.checked)}
                  className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <div>
                  <span className="block text-sm font-medium text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                    Make globally available
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    This simulation will be visible to all organizations
                  </span>
                </div>
              </label>

              {/* Programming simulation checkbox */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={isProgramming}
                  onChange={(e) => setIsProgramming(e.target.checked)}
                  className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <div>
                  <span className="block text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    Programming simulation
                  </span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">
                    This simulation involves coding or programming tasks
                  </span>
                </div>
              </label>

              {/* Tags */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Tags
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    placeholder="Enter tag and press Enter"
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="px-4 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 border border-indigo-300 dark:border-indigo-600 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                  >
                    Add
                  </button>
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1.5 px-3 py-1 text-sm bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 rounded-full"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="p-0.5 hover:bg-indigo-200 dark:hover:bg-indigo-800 rounded-full transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
              <button
                type="button"
                onClick={() => setIsSaveModalOpen(false)}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={isSaving || ((saveMode === 'new' || !canUpdateSimulation) && (!newSimulationName.trim() || newSimulationName.trim() === originalSimulationName))}
                className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  saveMode === 'update' && isEditMode && canUpdateSimulation ? 'Update Simulation' : 'Create Simulation'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

